// Externally Sent Emails tab — Session 80.
//
// Shared component used on both LeadDetail and ClientDetail. Shows the
// lead/client's unique BCC address (copy-pill) plus a chronological list
// of archived emails that arrived via that BCC.
//
// Props:
//   entityType: 'lead' | 'client'
//   entityId:   the lead_id or client.id
//   bccAddress: (leads) the address already returned by GET /crm/leads/{id};
//               when given, the tab does not fetch it again.
//
// Flow:
//   1. On mount, GET /api/email-archive/bcc-address  → BCC pill
//   2. On mount, GET /api/email-archive/list         → message list
//   3. Click a row → expanded panel with full body + attachment list
//
// File-and-forget — no notifications, no actions, just a chronological
// log so the user can find what was said and to whom.

import React, { useEffect, useState } from "react";
import { Copy, Check, Mail, Paperclip, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { SafeHtml } from "../lib/safeHtml";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export default function ExternallySentEmailsTab({ entityType, entityId, bccAddress }) {
  const [bccInfo, setBccInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedBody, setExpandedBody] = useState({});

  // The address is shown at the top of the tab for leads AND clients
  // (2026-10-28 — v1.54 hid it on leads and pointed at a "Dynamic Email"
  // row that rendered "-"). Leads pass the address in from the lead
  // payload; clients fetch it.
  const fetchBcc = bccAddress === undefined;

  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;
    setLoading(true);
    const calls = [
      api.get(`/email-archive/list?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`),
    ];
    if (fetchBcc) {
      calls.unshift(api.get(`/email-archive/bcc-address?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`));
    }
    Promise.all(calls)
      .then((results) => {
        if (cancelled) return;
        if (fetchBcc) {
          setBccInfo(results[0].data);
          setItems(results[1].data.items || []);
        } else {
          setItems(results[0].data.items || []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error("Could not load archived emails: " + (err?.response?.data?.detail || err.message));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType, entityId, fetchBcc]);

  const address = fetchBcc ? bccInfo?.bcc_address : bccAddress;

  const copyBcc = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success("BCC address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!expandedBody[id]) {
      try {
        const r = await api.get(`/email-archive/${id}`);
        setExpandedBody((b) => ({ ...b, [id]: r.data }));
      } catch (err) {
        toast.error("Could not load email body");
      }
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="externally-sent-emails-tab">
      <div className="bg-[#FFFBEB] border border-[#FCD34D] p-3" data-testid="bcc-archive-panel">
        <div className="flex items-start gap-3 flex-wrap">
          <Mail className="w-4 h-4 mt-1 text-[#D97706] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#92400E] font-semibold mb-1">
              BCC to Pulse
            </div>
            <div className="text-[12px] text-[#525252] mb-1.5">
              BCC this address when emailing this {entityType === "lead" ? "contact" : "client"} from Gmail or Outlook so the message is archived in Pulse.
            </div>
            {address ? (
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="text-[13px] font-mono bg-white border border-[#FCD34D] px-2 py-1 select-all break-all"
                  data-testid="bcc-address-value"
                >
                  {address}
                </code>
                <button
                  onClick={copyBcc}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[12px] bg-white border border-[#FCD34D] hover:bg-[#FEF3C7] transition-colors"
                  data-testid="bcc-address-copy-btn"
                  title="Copy BCC address"
                  aria-label="Copy BCC archive address"
                >
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
            ) : fetchBcc && loading ? (
              <div className="text-[12px] text-[#737373]">Loading…</div>
            ) : (
              <div className="text-[12px] text-[#B91C1C]" data-testid="bcc-address-unavailable">
                BCC archive address unavailable — refresh the page; if it persists, contact a platform admin.
              </div>
            )}
            <div className="text-[11px] text-[#737373] mt-1.5 leading-relaxed">
              Only email you send yourself belongs here — it is filed under this {entityType} and does not mark the lead as hot.
              The address is unique to this {entityType}; anyone with it can post to this archive, so don&apos;t share it externally.
            </div>
          </div>
        </div>
      </div>

      {/* Email list */}
      {loading ? (
        <div className="text-[12px] text-[#737373] py-8 text-center">Loading archived emails…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#E5E5E5] p-8 text-center text-[12px] text-[#737373]" data-testid="archived-emails-empty">
          No archived emails yet.<br />
          BCC the address above on your next external email to this {entityType} and it&apos;ll appear here.
        </div>
      ) : (
        <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5]" data-testid="archived-emails-list">
          {items.map((it) => {
            const isOpen = expandedId === it.id;
            const detail = expandedBody[it.id];
            return (
              <div key={it.id} className="hover:bg-[#FAFAFA] transition-colors">
                <button
                  onClick={() => toggleExpand(it.id)}
                  className="w-full text-left px-3 py-2.5 flex items-start gap-3"
                  data-testid={`archived-email-row-${it.id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {isOpen ? <ChevronUp className="w-4 h-4 text-[#737373]" /> : <ChevronDown className="w-4 h-4 text-[#737373]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="text-[13px] font-medium text-[#0A0A0A] truncate">
                        {it.from_name || it.from_email || "(unknown sender)"}
                      </div>
                      {it.from_name && it.from_email && (
                        <div className="text-[11px] text-[#A3A3A3] truncate">&lt;{it.from_email}&gt;</div>
                      )}
                      <div className="text-[11px] text-[#A3A3A3] ml-auto flex-shrink-0">{fmtDate(it.received_at)}</div>
                    </div>
                    <div className="text-[13px] text-[#262626] font-medium mt-0.5 truncate">{it.subject || "(no subject)"}</div>
                    {it.snippet && (
                      <div className="text-[12px] text-[#737373] mt-1 line-clamp-2">{it.snippet}</div>
                    )}
                    {it.attachment_summary && (
                      <div className="inline-flex items-center gap-1 text-[11px] text-[#525252] mt-1">
                        <Paperclip className="w-3 h-3" /> {it.attachment_summary}
                      </div>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-3 pb-4 pt-1 pl-10 border-t border-[#F5F5F5] bg-[#FAFAFA]" data-testid={`archived-email-body-${it.id}`}>
                    {!detail ? (
                      <div className="text-[12px] text-[#737373] py-3">Loading…</div>
                    ) : (
                      <>
                        <div className="text-[11px] text-[#737373] grid grid-cols-[80px_1fr] gap-y-0.5 mb-3 mt-2">
                          {detail.to_emails && (<><div>To:</div><div className="break-all">{detail.to_emails}</div></>)}
                          {detail.cc_emails && (<><div>Cc:</div><div className="break-all">{detail.cc_emails}</div></>)}
                          {detail.attachment_summary && (<><div>Attached:</div><div>{detail.attachment_summary}</div></>)}
                        </div>
                        {detail.body_html ? (
                          // Defence-in-depth (Session 81 follow-up):
                          // body_html was already sanitised on ingest by
                          // backend `html_sanitize.sanitize_rich_html`, but
                          // we additionally pipe it through the shared
                          // DOMPurify wrapper so any future regression in
                          // the backend sanitizer (or a row imported
                          // before sanitisation existed) cannot reach the
                          // browser DOM unscrubbed.
                          <SafeHtml
                            html={detail.body_html}
                            className="bg-white border border-[#E5E5E5] p-3 max-w-full overflow-auto text-[13px]"
                          />
                        ) : detail.body_text ? (
                          <pre className="bg-white border border-[#E5E5E5] p-3 max-w-full overflow-auto text-[12px] whitespace-pre-wrap font-sans">{detail.body_text}</pre>
                        ) : (
                          <div className="text-[12px] text-[#737373] italic">(empty body)</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
